// ===============================================
//  Транспорт на axios-клиенте (api-service) + наш слой:
//    • X-Access-Token инжектит requestInterceptor (apiFacade) из cookie
//    • 2-FA handshake (Google / SMS)
//    • single-flight refresh на 401 (не AuthService — без редиректа на emcd.io)
//    • токены в localStorage (webview-safe; cookie в TMA-webview режутся)
//    • centralised pre-loader + error handler
// ===============================================
import qs from "qs";
import { nanoid } from "nanoid";
import router from "@/router";
import { useUiStore } from "@/stores/ui";
import { useUserStore } from "@/stores/user";
import { api } from "@/api";
import { dataByPath } from "./demoData/dataByPath";
import { TG_AUTH_FAIL } from "./constants";
import { getTokens, setTokens, clearTokens } from "./tokens";

/* ------------------------------------------------------------------ helpers */
const PRELOADER_DELAY = 0; // ms – tweak if you want fade-in delay

const ui = () => useUiStore();
const user = () => useUserStore();

// axios-инстанс с requestInterceptor (X-Access-Token из cookie)
const client = () => api.facade?.endpointApi?.client;

// --------------------------------------------- refresh-token orchestration
let isRefreshing = false;
let refreshPromise = null;

async function performLogout() {
  // Полный сброс сессии (токены + pinia user + Vuex), затем экран ошибки
  const { resetSession } = await import("./auth");
  resetSession();
  router.replace({ name: "Error" });
}

async function triggerTokenRefresh() {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshPromise = (async () => {
      try {
        const { Auth } = await import("./auth");
        const res = await Auth.refresh();
        if (!res?.isError && res?.data) {
          setTokens(res.data);
          return true;
        }
        await Promise.reject(new Error("auth-refresh-failed"));
      } catch (e) {
        await performLogout();
        throw e;
      } finally {
        isRefreshing = false;
      }
    })();
  }
  return refreshPromise;
}

/* --------------------------------------------- pre-loader (global, tiny) */
const pending = new Set();
let timer = null;

function showLoader() {
  if (!timer) timer = setTimeout(ui().showPreloader, PRELOADER_DELAY);
}

function hideLoader(id) {
  pending.delete(id);
  if (!pending.size && timer) {
    clearTimeout(timer);
    timer = null;
    ui().hidePreloader();
  }
}

/* --------------------------------------------- 2-FA handshake container */
const twoFA = {
  secret: null,
  code: null,
  retry: null,
  params: null,
  set(fn, params) {
    this.retry = fn;
    this.params = params;
  },
  async run(code) {
    this.code = code;
    if (this.retry) await this.retry(this.params);
  },
  clear() {
    this.secret = this.code = this.retry = this.params = null;
  },
};

/* --------------------------------------------- axios-вызов + маппинг ошибок */
function buildHeaders(config) {
  const headers = { ...(config.headers || {}) };
  if (twoFA.code) {
    headers["Secret-Key"] = twoFA.secret || "";
    headers["Secret-Value"] = twoFA.code;
  }
  return headers;
}

// Один axios-запрос. Успех → {ok:true,data}; кидает axios-ошибку на не-2xx.
async function sendOnce(url, config) {
  const c = client();
  if (!c) throw new Error("api-facade-not-ready");
  const res = await c.request({
    url,
    method: config.method || "GET",
    data: config.body,
    headers: buildHeaders(config),
  });
  return { ok: true, data: res.data };
}

/* ------------------------------------------------------------------ core */
async function request(
  url,
  config = {},
  {
    disablePreloader = false,
    skipAuthRefreshGate = false,
    silentError = false,
  } = {},
) {
  // DEMO-mode short-circuit
  const path = new URL(url).pathname;
  if (user().authNeedEmail && dataByPath[path]) {
    return { ok: true, data: dataByPath[path] };
  }

  const id = nanoid();
  if (!disablePreloader) {
    pending.add(id);
    showLoader();
  }

  // Если идёт refresh — ждём его завершения и не шлём запросы
  if (isRefreshing && !skipAuthRefreshGate) {
    try {
      await refreshPromise;
    } catch (e) {
      hideLoader(id);
      return { isError: true };
    }
  }

  try {
    /* ---------- SUCCESS ---------- */
    const ok = await sendOnce(url, config);
    twoFA.clear();
    hideLoader(id);
    return ok;
  } catch (e) {
    // Транспорт (axios) кидает на не-2xx и на сетевых ошибках.
    const status = e?.response?.status;
    const err = e?.response?.data ?? {};

    // Сетевая/неизвестная ошибка (нет response) — как раньше catch-all
    if (!e?.response) {
      hideLoader(id);
      if (!silentError) ui().addError(err);
      return { isError: true };
    }

    const unauth =
      status === 401 ||
      (err?.error && err.error.toLowerCase?.() === "invalid token") ||
      err?.code === "auth-0003";
    const tgAuthFail = err?.code === TG_AUTH_FAIL;

    // Ошибка телеграм-авторизации
    if (tgAuthFail) {
      handleAuthErrors(status, err);
      handle2FA(err);
      hideLoader(id);
      err.isError = true;
      return err;
    }

    // Демо-режим (неавторизованный TG-юзер без email): при 401 не рефрешим и не
    // разлогиниваем. Молчим на фоновых чтениях; для мутаций — ошибка в UI + 2FA.
    if (unauth && user().authNeedEmail && !skipAuthRefreshGate) {
      const isRead = (config.method ?? "GET").toUpperCase() === "GET";
      hideLoader(id);
      if (!isRead) {
        handle2FA(err);
        ui().addError(err ?? {});
      }
      return { ...(err ?? {}), isError: true };
    }

    // При неавторизованности — обновляем токен и повторяем запрос
    if (unauth && !skipAuthRefreshGate) {
      try {
        await triggerTokenRefresh();
        // Ретрай: новый токен уже в cookie, requestInterceptor подставит его сам.
        const retry = await sendOnce(url, config);
        twoFA.clear();
        hideLoader(id);
        return retry;
      } catch (e2) {
        // Либо refresh упал (уже разлогинены), либо ретрай вернул ошибку.
        hideLoader(id);
        if (!e2?.response) return { isError: true };
        const retryErr = e2.response.data ?? {};
        handle2FA(retryErr);
        if (retryErr.code !== TG_AUTH_FAIL) ui().addError(retryErr);
        retryErr.isError = true;
        return retryErr;
      }
    }

    // Рефреш-запрос вернул неавторизованность — выходим
    if (unauth && skipAuthRefreshGate) {
      await performLogout();
      hideLoader(id);
      return { isError: true };
    }

    // Прочие ошибки
    handle2FA(err);
    hideLoader(id);
    if (!silentError && err.code !== TG_AUTH_FAIL) ui().addError(err);
    err.isError = true;
    err.status = status;
    return err;
  }
}

/* ---------------------------------------------------------------- actions */
export const Request = {
  post(url, body, headers, o = {}) {
    return request(url, { method: "POST", body, headers }, o);
  },
  put(url, body, headers, o = {}) {
    return request(url, { method: "PUT", body, headers }, o);
  },
  delete(url, body, headers, o = {}) {
    return request(url, { method: "DELETE", body, headers }, o);
  },
  get(url, params, o = {}) {
    const q = params
      ? `?${qs.stringify(params, { arrayFormat: "brackets" })}`
      : "";
    return request(url + q, { method: "GET" }, o);
  },
  patch(url, body, headers, o = {}) {
    return request(url, { method: "PATCH", body, headers }, o);
  },
  /* 2-FA helpers -------------------------------------------------------- */
  set2Fa(fn, params) {
    twoFA.set(fn, params);
  },
  async tryLastReq(code) {
    await twoFA.run(code);
  },
  reset2Fa() {
    twoFA.clear();
  },
  getTokens,
};

/* ------------------------------------------------------------------ utils */
function handleAuthErrors(status, err) {
  const unauth =
    status === 401 ||
    (err?.error && err.error.toLowerCase?.() === "invalid token") ||
    err?.code === "auth-0003" ||
    err?.code === TG_AUTH_FAIL;

  if (!unauth) return;

  clearTokens();

  if (err?.code !== TG_AUTH_FAIL) {
    router.replace({ name: "Error" });
  }
}

function handle2FA(err) {
  if (!err?.required) return;

  switch (err.required[0]) {
    case "2fa-google":
      twoFA.secret = err.secret_key;
      twoFA.code = null;
      router.replace({ name: "2FA", query: { type: "2FA" } });
      break;
    case "2fa-sms":
      twoFA.secret = err.secret_key;
      twoFA.code = null;
      router.replace({ name: "2FA", query: { type: "phone" } });
      break;
    default:
      break;
  }
}
